import {AxiosError, AxiosInstance} from 'axios';
import {notify} from '../../../stores/notificationStore';
import {refreshSession} from '../../auth/refreshSession';

const TOAST_ID = 'session-unauthorized';
const REFRESHED_TOAST_DURATION_MS = 8000;

let sessionLostHandler: (() => void) | null = null;

/**
 * Registers how to start sign-in when the session is gone for good (the refresh itself was
 * unauthorized). Set by AuthProvider, which owns the sign-in redirect; null when no provider is
 * mounted.
 */
export function setSessionLostHandler(handler: (() => void) | null): void {
  sessionLostHandler = handler;
}

function notifySessionLost(): void {
  // No session left to refresh (cookies cleared, revoked or expired mid-session). A retry won't fix
  // it, but navigating away on the user's behalf could discard unsaved work (a background poll can
  // be what hits the 401), so offer sign-in and leave the page alone until they choose.
  notify({
    id: TOAST_ID,
    type: 'Toast',
    toastType: 'warning',
    title: 'Your session has ended',
    description: 'Sign in again to continue. You will return to this page.',
    toastOptions: {
      id: TOAST_ID,
      duration: Infinity,
      action: {label: 'Sign in', onClick: () => sessionLostHandler?.()},
    },
  });
}

async function refreshAndNotify(): Promise<void> {
  const {outcome} = await refreshSession();

  if (outcome === 'unauthorized') {
    notifySessionLost();
    return;
  }

  notify({
    id: TOAST_ID,
    type: 'Toast',
    toastType: 'warning',
    title: 'That request failed',
    description:
      outcome === 'refreshed'
        ? 'Your session had expired and has been refreshed. Please try again.'
        : 'Your session could not be refreshed. Please try again. If it keeps failing, close and reopen this tab/window, or sign in again.',
    toastOptions: {
      // notify() never feeds its own id to Sonner, so pass it here; a second toast with the same
      // id updates the first in place instead of stacking.
      id: TOAST_ID,
      // A refreshed session needs nothing but a retry, so it clears itself; a failed refresh
      // needs the user to act, so it stays until dismissed.
      duration:
        outcome === 'refreshed' ? REFRESHED_TOAST_DURATION_MS : Infinity,
      action: {label: 'Dismiss', onClick: () => {}},
    },
  });
}

/**
 * When a domain API answers 401 (e.g. the WorkOS token the backend needs for Workato is missing
 * while the ResToken still authenticates), refresh the session in the background and tell the
 * user the request failed and what to do about it. The request is never retried (a 401 can come
 * partway through a handler, so there is no guarantee it is safe to replay) and nothing
 * redirects on its own, so the user keeps their place. The original error is rejected
 * immediately so the caller can react. The fixed toast id means a burst of failing calls shows
 * one toast, and the shared refresh means they trigger one refresh.
 *
 * Skips /auth/* requests, which AuthContext already handles. When the refresh itself is
 * unauthorized there is no session left, so the toast says so and offers a Sign in action, which
 * calls the session-lost handler (the sign-in redirect, returning to the current page).
 */
export function addUnauthorizedInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(undefined, (error: AxiosError) => {
    const isAuthRequest = !!error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthRequest) {
      void refreshAndNotify();
    }
    return Promise.reject(error);
  });
}
